import { create, StoreApi } from 'zustand';
import { Email, Mailbox } from '@/types/jmap.types';

export interface EmailListCacheKeyParams {
  mailbox: string;
  searchQuery?: string;
  searchFilters?: object;
  starred?: boolean;
}

/**
 * Canonical cache key for the email list. Shared between EmailList (which writes
 * the cache after a fetch) and the store (which mutates the cache when SSE
 * delta-loads new mail). Keep both call sites pointing at this builder.
 */
export const buildEmailListCacheKey = (params: EmailListCacheKeyParams): string =>
  JSON.stringify({
    mailbox: params.mailbox,
    searchQuery: params.searchQuery || '',
    searchFilters: params.searchFilters || {},
    starred: params.starred || false,
  });

interface EmailListCacheEntry {
  emails: Email[];
  fetchedAt: number;
}

interface EmailDetailCacheEntry {
  email: Email;
  fetchedAt: number;
}

interface UserAvatarCacheEntry {
  url: string | null;
  fetchedAt: number;
}

/**
 * Best-effort revoke for blob: URLs returned by URL.createObjectURL. Anything
 * else (null, http(s), data:) is left alone. Centralized so every code path
 * that overwrites or clears an avatar URL releases the underlying blob —
 * without this, every viewed sender added a permanently-pinned blob to the
 * tab's memory.
 */
const releaseBlobUrl = (url: string | null | undefined): void => {
  if (!url || typeof url !== 'string') return;
  if (!url.startsWith('blob:')) return;
  try { URL.revokeObjectURL(url); } catch {}
};

// Cache size caps. JS objects preserve insertion order, so popping the
// first key gives us a cheap FIFO eviction — close enough to LRU for these
// use cases without the extra bookkeeping.
const EMAIL_LIST_CACHE_MAX = 25;     // distinct mailboxes × search variants
const EMAIL_DETAIL_CACHE_MAX = 100;  // recently-viewed full emails
const USER_AVATAR_CACHE_MAX = 200;   // recently-seen sender avatars

/**
 * Drop the oldest entries until the record is at or below `max - 1` keys,
 * leaving room for the caller to insert one more. Returns a new object;
 * does not mutate. `onEvict` runs once per evicted entry — used to release
 * blob URLs before they're orphaned.
 */
function evictOldest<T>(
  record: Record<string, T>,
  max: number,
  onEvict?: (key: string, value: T) => void
): Record<string, T> {
  const keys = Object.keys(record);
  if (keys.length < max) return record;
  const toDrop = keys.length - (max - 1);
  if (toDrop <= 0) return record;
  const next = { ...record };
  for (let i = 0; i < toDrop; i++) {
    const key = keys[i];
    if (onEvict) onEvict(key, next[key]);
    delete next[key];
  }
  return next;
}

interface EmailState {
  mailboxes: Mailbox[];
  emails: Email[];
  emailListCache: Record<string, EmailListCacheEntry>;
  emailDetailCache: Record<string, EmailDetailCacheEntry>;
  favoriteMailboxIds: string[];
  selectedMailbox: string | null;
  selectedEmail: string | null;
  drawerOpen: boolean;
  newMailCount: number;
  newCalendarCount: number;
  mailListRefreshToken: number;
  accountEmail: string | null;
  accountDisplayName: string | null;
  accountAvatarUrl: string | null;
  userAvatarCache: Record<string, UserAvatarCacheEntry>;
  setMailboxes: (mailboxes: Mailbox[]) => void;
  setEmails: (emails: Email[]) => void;
  setEmailListCache: (key: string, emails: Email[], fetchedAt?: number) => void;
  setEmailDetailCache: (email: Email, fetchedAt?: number) => void;
  clearEmailListCache: (key?: string) => void;
  clearEmailDetailCache: (emailId?: string) => void;
  setFavoriteMailboxIds: (ids: string[]) => void;
  toggleFavoriteMailbox: (mailboxId: string) => void;
  selectMailbox: (mailboxId: string) => void;
  selectEmail: (emailId: string | null) => void;
  removeEmail: (emailId: string) => void;
  updateEmail: (emailId: string, updates: Partial<Email>) => void;
  toggleDrawer: () => void;
  setDrawerOpen: (open: boolean) => void;
  incrementNewMailCount: (count?: number) => void;
  resetNewMailCount: () => void;
  /**
   * Merge newly-arrived emails into the in-memory list and the cached entry for
   * the given mailbox. Deduplicates by id; preserves all already-loaded emails.
   */
  mergeNewerEmails: (mailboxId: string, newEmails: Email[]) => void;
  /**
   * Optimistically bump (or decrement, with negative delta) a mailbox's
   * unread / total counters. Used by the SSE notification handler so the
   * sidebar badge updates the moment a new mail event arrives, even if the
   * subsequent JMAP delta-fetch hasn't caught up with indexing yet.
   */
  bumpMailboxCounts: (mailboxId: string, delta: { unread?: number; total?: number }) => void;
  incrementNewCalendarCount: (count?: number) => void;
  resetNewCalendarCount: () => void;
  bumpMailListRefresh: () => void;
  setAccountProfile: (email: string | null, displayName?: string | null, avatarUrl?: string | null) => void;
  setAccountAvatarUrl: (avatarUrl: string | null) => void;
  setUserAvatarCache: (email: string, url: string | null, fetchedAt?: number) => void;
  clearUserAvatarCache: (email?: string) => void;
  resetStore: () => void;
}

const loadFavoriteMailboxIds = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem('webmail_favorite_mailboxes');
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

const persistFavoriteMailboxIds = (ids: string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('webmail_favorite_mailboxes', JSON.stringify(ids));
  } catch {}
};

const clearFavoriteMailboxIds = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('webmail_favorite_mailboxes');
  } catch {}
};

type SetState = StoreApi<EmailState>['setState'];

export const useEmailStore = create<EmailState>((set: SetState) => ({
  mailboxes: [],
  emails: [],
  emailListCache: {},
  emailDetailCache: {},
  favoriteMailboxIds: loadFavoriteMailboxIds(),
  selectedMailbox: null,
  selectedEmail: null,
  drawerOpen: false,
  newMailCount: 0,
  newCalendarCount: 0,
  mailListRefreshToken: 0,
  accountEmail: null,
  accountDisplayName: null,
  accountAvatarUrl: null,
  userAvatarCache: {},
  setMailboxes: (mailboxes: Mailbox[]) => set({ mailboxes }),
  setEmails: (emails: Email[]) =>
    set({
      emails: [...emails].sort((a: Email, b: Email) => {
        const da = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const db = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return db - da; // newest first
      }),
    }),
  setEmailListCache: (key: string, emails: Email[], fetchedAt: number = Date.now()) =>
    set((state: EmailState) => {
      // Re-inserting an existing key updates in place; only evict when adding
      // a brand-new key so the working set stays warm for the user.
      const base = key in state.emailListCache
        ? state.emailListCache
        : evictOldest(state.emailListCache, EMAIL_LIST_CACHE_MAX);
      return { emailListCache: { ...base, [key]: { emails, fetchedAt } } };
    }),
  setEmailDetailCache: (email: Email, fetchedAt: number = Date.now()) =>
    set((state: EmailState) => {
      const base = email.id in state.emailDetailCache
        ? state.emailDetailCache
        : evictOldest(state.emailDetailCache, EMAIL_DETAIL_CACHE_MAX);
      return { emailDetailCache: { ...base, [email.id]: { email, fetchedAt } } };
    }),
  clearEmailListCache: (key?: string) =>
    set((state: EmailState) => {
      if (!key) return { emailListCache: {} };
      const next = { ...state.emailListCache };
      delete next[key];
      return { emailListCache: next };
    }),
  clearEmailDetailCache: (emailId?: string) =>
    set((state: EmailState) => {
      if (!emailId) return { emailDetailCache: {} };
      const next = { ...state.emailDetailCache };
      delete next[emailId];
      return { emailDetailCache: next };
    }),
  setFavoriteMailboxIds: (ids: string[]) => {
    persistFavoriteMailboxIds(ids);
    set({ favoriteMailboxIds: ids });
  },
  toggleFavoriteMailbox: (mailboxId: string) =>
    set((state: EmailState) => {
      const exists = state.favoriteMailboxIds.includes(mailboxId);
      const next = exists
        ? state.favoriteMailboxIds.filter((id: string) => id !== mailboxId)
        : [...state.favoriteMailboxIds, mailboxId];
      persistFavoriteMailboxIds(next);
      return { favoriteMailboxIds: next };
    }),
  selectMailbox: (mailboxId: string) =>
    set((state: EmailState) => {
      if (state.selectedMailbox === mailboxId) return state;
      return { selectedMailbox: mailboxId, emails: [] };
    }),
  selectEmail: (emailId: string | null) => set({ selectedEmail: emailId }),
  removeEmail: (emailId: string) =>
    set((state: EmailState) => {
      const nextCacheEntries: [string, EmailListCacheEntry][] = Object.entries(state.emailListCache).map(
        ([key, entry]: [string, EmailListCacheEntry]) => {
          return [key, { ...entry, emails: entry.emails.filter((email: Email) => email.id !== emailId) }];
        }
      );
      return {
        emails: state.emails.filter((email: Email) => email.id !== emailId),
        selectedEmail: state.selectedEmail === emailId ? null : state.selectedEmail,
        emailDetailCache: Object.keys(state.emailDetailCache).length
          ? Object.fromEntries(
              Object.entries(state.emailDetailCache).filter(([key]: [string, EmailDetailCacheEntry]) => key !== emailId)
            )
          : state.emailDetailCache,
        emailListCache: Object.fromEntries(nextCacheEntries),
      };
    }),
  updateEmail: (emailId: string, updates: Partial<Email>) =>
    set((state: EmailState) => {
      const applyUpdates = (email: Email): Email => {
        if (email.id !== emailId) return email;
        // Merge keywords map carefully to avoid losing other flags
        if (updates.keywords) {
          const merged: Record<string, boolean> = { ...(email.keywords || {}) };
          Object.entries(updates.keywords).forEach(([k, v]: [string, boolean | null]) => {
            if (v === null) {
              delete merged[k];
            } else {
              merged[k] = v;
            }
          });
          return { ...email, ...updates, keywords: merged };
        }
        return { ...email, ...updates };
      };
      const nextCacheEntries: [string, EmailListCacheEntry][] = Object.entries(state.emailListCache).map(
        ([key, entry]: [string, EmailListCacheEntry]) => {
          return [key, { ...entry, emails: entry.emails.map(applyUpdates) }];
        }
      );
      const existingDetail = state.emailDetailCache[emailId];
      const nextDetailCache = existingDetail
        ? { ...state.emailDetailCache, [emailId]: { ...existingDetail, email: applyUpdates(existingDetail.email) } }
        : state.emailDetailCache;
      return {
        emails: state.emails.map(applyUpdates),
        emailListCache: Object.fromEntries(nextCacheEntries),
        emailDetailCache: nextDetailCache,
      };
    }),
  toggleDrawer: () => set((state: EmailState) => ({ drawerOpen: !state.drawerOpen })),
  setDrawerOpen: (open: boolean) => set({ drawerOpen: open }),
  incrementNewMailCount: (count: number = 1) =>
    set((state: EmailState) => ({ newMailCount: state.newMailCount + Math.max(count, 0) })),
  resetNewMailCount: () => set({ newMailCount: 0 }),
  mergeNewerEmails: (mailboxId: string, newEmails: Email[]) =>
    set((state: EmailState) => {
      if (!newEmails.length) return state;
      const sortByReceived = (a: Email, b: Email) =>
        new Date(b.receivedAt || 0).getTime() - new Date(a.receivedAt || 0).getTime();

      // Update every cache entry that targets this mailbox without an active
      // search/filter (search results stay as the user left them).
      const nextCache = { ...state.emailListCache };
      for (const [key, entry] of Object.entries(state.emailListCache)) {
        let parsed: EmailListCacheKeyParams | null = null;
        try { parsed = JSON.parse(key); } catch { parsed = null; }
        if (!parsed || parsed.mailbox !== mailboxId) continue;
        if (parsed.searchQuery || (parsed.searchFilters && Object.keys(parsed.searchFilters).length)) continue;
        const existingIds = new Set(entry.emails.map((e) => e.id));
        const trulyNew = newEmails.filter((e) => !existingIds.has(e.id));
        if (!trulyNew.length) continue;
        nextCache[key] = {
          emails: [...trulyNew, ...entry.emails].sort(sortByReceived),
          // Refresh the timestamp so the cache stays "warm" instead of forcing
          // a full reload right after a delta merge.
          fetchedAt: Date.now(),
        };
      }

      // Update the visible list only when the user is currently looking at this
      // mailbox; otherwise leave it alone (the cache will be applied on switch).
      let nextEmails = state.emails;
      if (state.selectedMailbox === mailboxId) {
        const visibleIds = new Set(state.emails.map((e) => e.id));
        const trulyNew = newEmails.filter((e) => !visibleIds.has(e.id));
        if (trulyNew.length) {
          nextEmails = [...trulyNew, ...state.emails].sort(sortByReceived);
        }
      }

      return { emailListCache: nextCache, emails: nextEmails };
    }),
  incrementNewCalendarCount: (count: number = 1) =>
    set((state: EmailState) => ({ newCalendarCount: state.newCalendarCount + Math.max(count, 0) })),
  resetNewCalendarCount: () => set({ newCalendarCount: 0 }),
  bumpMailListRefresh: () => set((state: EmailState) => ({ mailListRefreshToken: state.mailListRefreshToken + 1 })),
  bumpMailboxCounts: (mailboxId: string, delta: { unread?: number; total?: number }) =>
    set((state: EmailState) => {
      const unreadDelta = delta.unread ?? 0;
      const totalDelta = delta.total ?? 0;
      if (!unreadDelta && !totalDelta) return state;
      const next = state.mailboxes.map((mb) => {
        if (mb.id !== mailboxId) return mb;
        return {
          ...mb,
          unreadEmails: Math.max(0, (mb.unreadEmails || 0) + unreadDelta),
          totalEmails: Math.max(0, (mb.totalEmails || 0) + totalDelta),
        };
      });
      return { mailboxes: next };
    }),
  setAccountProfile: (email: string | null, displayName: string | null = null, avatarUrl: string | null = null) =>
    set((state: EmailState) => {
      const accountChanged = state.accountEmail && state.accountEmail !== email;
      // If the account itself changed, drop the cache so we don't leak blob
      // URLs from the previous user's session.
      if (accountChanged) {
        Object.values(state.userAvatarCache).forEach((entry) => releaseBlobUrl(entry?.url));
      }
      // Replace the account avatar — release the previous one first.
      if (state.accountAvatarUrl !== avatarUrl) {
        releaseBlobUrl(state.accountAvatarUrl);
      }
      return {
        accountEmail: email,
        accountDisplayName: displayName,
        accountAvatarUrl: avatarUrl,
        userAvatarCache: accountChanged ? {} : state.userAvatarCache,
      };
    }),
  setAccountAvatarUrl: (avatarUrl: string | null) =>
    set((state: EmailState) => {
      if (state.accountAvatarUrl !== avatarUrl) {
        releaseBlobUrl(state.accountAvatarUrl);
      }
      return { accountAvatarUrl: avatarUrl };
    }),
  setUserAvatarCache: (email: string, url: string | null, fetchedAt: number = Date.now()) =>
    set((state: EmailState) => {
      const previous = state.userAvatarCache[email]?.url;
      if (previous && previous !== url) {
        releaseBlobUrl(previous);
      }
      // Evict on new-key inserts so a long-lived session that has scrolled
      // past hundreds of senders doesn't pin every avatar's blob in memory.
      const base = email in state.userAvatarCache
        ? state.userAvatarCache
        : evictOldest(state.userAvatarCache, USER_AVATAR_CACHE_MAX, (_k, v) => releaseBlobUrl(v?.url));
      return {
        userAvatarCache: { ...base, [email]: { url, fetchedAt } },
      };
    }),
  clearUserAvatarCache: (email?: string) =>
    set((state: EmailState) => {
      if (!email) {
        Object.values(state.userAvatarCache).forEach((entry) => releaseBlobUrl(entry?.url));
        return { userAvatarCache: {} };
      }
      const previous = state.userAvatarCache[email]?.url;
      releaseBlobUrl(previous);
      const next = { ...state.userAvatarCache };
      delete next[email];
      return { userAvatarCache: next };
    }),
  resetStore: () =>
    set((state: EmailState) => {
      clearFavoriteMailboxIds();
      // Release every blob URL we own before tearing the store down on logout.
      releaseBlobUrl(state.accountAvatarUrl);
      Object.values(state.userAvatarCache).forEach((entry) => releaseBlobUrl(entry?.url));
      return {
        mailboxes: [],
        emails: [],
        emailListCache: {},
        emailDetailCache: {},
        favoriteMailboxIds: [],
        selectedMailbox: null,
        selectedEmail: null,
        drawerOpen: false,
        newMailCount: 0,
        newCalendarCount: 0,
        mailListRefreshToken: 0,
        accountEmail: null,
        accountDisplayName: null,
        accountAvatarUrl: null,
        userAvatarCache: {},
      };
    }),
}));
